import React, { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Menu,
  MenuButton,
  Button,
  Box,
  Icon,
  Text,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { IoMdAdd } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import { NodeInsights } from "containers/Interfaces";
import {
  BenchmarksIdsToNameMap,
  ActualsIdToMetricNameMap,
  getMetricFormat,
  isMaterialColumn,
  formatWithProto,
  ActualsMap,
  showFilterForVersusBenchmark,
  showFilterForMetric,
  getAnalysisColumnDisplayName,
  Suggestions,
  SuggestionMap,
} from "./dataTableUtils";

interface MeltedDataTableProps {
  node: NodeInsights;
  isChatOpen: boolean;
  benchmarksIdNameMap: BenchmarksIdsToNameMap;
  actualsIdNameMap: ActualsIdToMetricNameMap;
  actualsMap: ActualsMap;
  handleMenuClose: () => void;
  handleMenuOpen: (index: number, actualId: string) => void;
  selectedMeltedRowId: string | null;
  listOfSuggestions: Suggestions;
  newSuggestionMap: SuggestionMap;
  setNewSuggestionMap: (newSuggestionMap: SuggestionMap) => void;
  showEditSuggestions?: boolean;
  treePannelLoader?: boolean;
  isOverlay?: boolean;
}

// Sort analysis types to show variance first, then variance percentage, then others
const sortAnalysisTypes = (types: Set<string>): string[] => {
  return Array.from(types).sort((a, b) => {
    if (a === "VARIANCE" && b !== "VARIANCE") return -1;
    if (a !== "VARIANCE" && b === "VARIANCE") return 1;
    if (a === "VARIANCE_PERCENTAGE" && b !== "VARIANCE_PERCENTAGE") return -1;
    if (a !== "VARIANCE_PERCENTAGE" && b === "VARIANCE_PERCENTAGE") return 1;
    return a.localeCompare(b);
  });
};

export const MeltedDataTable: React.FC<MeltedDataTableProps> = ({
  node,
  isChatOpen,
  benchmarksIdNameMap,
  actualsIdNameMap,
  actualsMap,
  handleMenuClose,
  handleMenuOpen,
  selectedMeltedRowId,
  listOfSuggestions,
  newSuggestionMap,
  setNewSuggestionMap,
  showEditSuggestions = true,
  treePannelLoader,
  isOverlay = false
}) => {
  const [expandedBenchmarks, setExpandedBenchmarks] = useState<Record<string, boolean>>({});
  const [expandedActual, setExpandedActual] = useState<boolean>(false);
  
  // Helper function to check if an actualId is ONLY a material (not a standalone metric)
  const isOnlyMaterialId = (actualId: string) => {
    // Check if this actualId is used as material for other metrics
    const isUsedAsMaterial = node.current_node?.kg_observation?.metrics.some(metric => 
      metric.material?.header?.id === actualId
    ) || false;
    
    // Check if this actualId is also a standalone metric (has its own metric definition)
    const isStandaloneMetric = node.current_node?.kg_observation?.metrics.some(metric => 
      metric.header?.id === actualId
    ) || false;
    
    // Only filter out if it's used as material AND is not a standalone metric
    return isUsedAsMaterial && !isStandaloneMetric;
  };

  // Filter out entries that are ONLY materials (not standalone metrics)
  const meltedRows = Object.entries(actualsMap)
    .filter(([actualId]) => !isOnlyMaterialId(actualId)) // Keep standalone metrics even if they're also used as materials
    .map(([actualId, actualData]) => ({
      actualId,
      metricName: actualsIdNameMap[actualId].name,
      actualsValue: actualData.actual_value,
      analysisValue: actualData.analysis_value,
      benchmarks: actualData.benchmarks,
      type: actualsIdNameMap[actualId].type
    }));

  // Check if any metric has actual material data in the actualsMap
  const hasMaterials = meltedRows.some(row => {
    const actualData = actualsMap[row.actualId];
    return actualData?.material_value && actualData.material_value.length > 0;
  });

  // Check if any metric has analysis data in the actualsMap
  const hasAnalysis = meltedRows.some(row => {
    return row.analysisValue && Object.keys(row.analysisValue).length > 0;
  });

  // Collect all benchmark IDs
  const benchmarkIds = new Set<string>();
  Object.values(actualsMap).forEach((actualData) => {
    Object.keys(actualData.benchmarks).forEach(bid => benchmarkIds.add(bid));
  });
  
  // Sort melted rows by type (metric first, then measure)
  const sortedMeltedRows = [...meltedRows].sort((a, b) => {
    if (a.type === "metric" && b.type !== "metric") return -1;
    if (a.type !== "metric" && b.type === "metric") return 1;
    return a.metricName.localeCompare(b.metricName);
  });

  const renderHeaderColumns = () => {
    const columns = [];
    
    // Metric column
    columns.push(
      <Th 
        key="metric"
        height="40px" 
        minHeight="40px" 
        verticalAlign="middle" 
        fontSize="sm" 
        p={2} 
        whiteSpace="nowrap" 
        overflow="hidden" 
        textOverflow="ellipsis"
        width="200px"
        minWidth="150px"
        maxWidth="300px"
        position="sticky"
        top="0"
        bg="white"
        zIndex={12}
        left="0"
      >
        Metric
      </Th>
    );
    
    // Actual Value column
    columns.push(
      <Th 
        key="actual"
        color="gray.600"
        fontSize={isChatOpen ? "2xs" : "xs"}
        bg="gray.50"
        fontWeight="700"
        textAlign="right"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        position="sticky"
        top="0"
        px={2}
        py={isChatOpen ? 1 : 1}
        left="auto"
        zIndex={10}
        width="140px"
        minWidth="120px"
        maxWidth="180px"
        cursor={hasMaterials ? "pointer" : "default"}
        onClick={hasMaterials ? () => setExpandedActual(!expandedActual) : undefined}
      >
        Actual Value
        {hasMaterials && (
          expandedActual ? <ChevronDownIcon ml={1} /> : <ChevronRightIcon ml={1} />
        )}
      </Th>
    );

    // Material column - only show if any metric has materials AND expanded
    if (hasMaterials && expandedActual) {
      columns.push(
        <Th 
          key="material"
          color="gray.600"
          fontSize={isChatOpen ? "2xs" : "xs"}
          bg="gray.50"
          fontWeight="700"
          textAlign="right"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          position="sticky"
          top="0"
          px={2}
          py={isChatOpen ? 1 : 1}
          left="auto"
          zIndex={10}
          width="130px"
          minWidth="110px"
          maxWidth="150px"
        >
          Materiality
        </Th>
      );
    }

    // Analysis columns - show if any metric has analysis data
    if (hasAnalysis) {
      // Collect all analysis types across all rows
      const analysisTypes = new Set<string>();
      meltedRows.forEach(row => {
        if (row.analysisValue) {
          Object.keys(row.analysisValue).forEach(type => {
            analysisTypes.add(type);
          });
        }
      });

      // Add header for each analysis type
      const sortedAnalysisTypes = sortAnalysisTypes(analysisTypes);
      sortedAnalysisTypes.forEach(analysisType => {
        columns.push(
          <Th
            key={`analysis-${analysisType}`}
            color="gray.600"
            fontSize={isChatOpen ? "2xs" : "xs"}
            bg="gray.50"
            fontWeight="700"
            textAlign="right"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            position="sticky"
            top="0"
            px={2}
            py={isChatOpen ? 1 : 1}
            left="auto"
            zIndex={10}
            width="120px"
            minWidth="100px"
            maxWidth="140px"
          >
            {getAnalysisColumnDisplayName(analysisType)}
          </Th>
        );
      });
    }
      
    // Add all benchmarks directly
    Array.from(benchmarkIds).forEach(benchmarkId => {
      // Add benchmark column
      columns.push(
        <Th 
          key={benchmarkId} 
          cursor="pointer" 
          onClick={() => {
            setExpandedBenchmarks(prev => ({
              ...prev,
              [benchmarkId]: !prev[benchmarkId]
            }));
          }}
          color="gray.600"
          fontSize={isChatOpen ? "2xs" : "xs"}
          bg="gray.50"
          fontWeight="700"
          textAlign="right"
          position="sticky"
          top="0"
          px={2}
          py={isChatOpen ? 1 : 1}
          left="auto"
          zIndex={10}
          width="130px"
          minWidth="110px"
          maxWidth="180px"
        >
          {benchmarksIdNameMap && benchmarksIdNameMap[benchmarkId] ? 
            benchmarksIdNameMap[benchmarkId] : "Benchmark " + benchmarkId.substr(0,3)}{" "}
          <Tooltip
            label={showFilterForVersusBenchmark(benchmarkId, node.current_node.kg_observation!)}
            placement="top"
          >
            <IconButton 
              aria-label="Filters"
              icon={<FiFilter size="10px" />}
              size="xs"
              variant="ghost"
              minW="16px"
              height="16px"
              p={0}
            />
          </Tooltip>
          {expandedBenchmarks[benchmarkId] ? <ChevronDownIcon ml={1} /> : <ChevronRightIcon ml={1} />}
        </Th>
      );
      
      // If expanded, add analysis columns for this benchmark
      if (expandedBenchmarks[benchmarkId]) {
        // Get all available analysis types for this benchmark
        const analysisTypes = new Set<string>();
        sortedMeltedRows.forEach(row => {
          if (row.benchmarks[benchmarkId]?.analysis_value) {
            Object.keys(row.benchmarks[benchmarkId].analysis_value).forEach(type => {
              analysisTypes.add(type);
            });
          }
        });

        // Add header for each analysis type
        const sortedAnalysisTypes = sortAnalysisTypes(analysisTypes);
        sortedAnalysisTypes.forEach(analysisType => {
          columns.push(
            <Th
              key={`${benchmarkId}-${analysisType}`}
              color="gray.600"
              fontSize={isChatOpen ? "2xs" : "xs"}
              bg="gray.50"
              fontWeight="700"
              textAlign="right"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              position="sticky"
              top="0"
              px={2}
              py={isChatOpen ? 1 : 1}
              left="auto"
              zIndex={10}
              width="120px"
              minWidth="100px"
              maxWidth="140px"
            >
              {getAnalysisColumnDisplayName(analysisType)}
            </Th>
          );
        });
      }
    });
    
    return columns;
  };

  const renderRowCells = (row: any) => {
    const cells = [];
    const formatProto = getMetricFormat(row.actualId, node.current_node, isMaterialColumn(row.actualId, node.current_node));

    // finding the material name for the metric
    const metric = node.current_node?.kg_observation?.metrics.find(m => m.header?.id === row.actualId);
    const materialName = metric?.material?.header?.name || "Material";

    // Metric cell
    cells.push(
      <Td 
        key="metric"
        color="inherit"
        textAlign="left"
        p={0}
        pr={0}
        m={0}
        width="200px"
        minWidth="150px"
        maxWidth="300px"
        whiteSpace="normal"
        overflow="visible"
        textOverflow="ellipsis"
        position="sticky"
        left="0"
        zIndex={12}
        cursor="pointer"
        bg="white"
      > 
        {showEditSuggestions ? (
          <Menu closeOnSelect={false} placement="bottom-start" flip={true} onClose={handleMenuClose} isOpen={selectedMeltedRowId === row.actualId}>
            <MenuButton
              as={Button}
              sx={{ overflow: "visible" }}
              rightIcon={
                <Box _groupHover={{ "& > svg": { boxSize: 5, color: "white" } }}>
                  <Icon 
                    as={IoMdAdd} 
                    color="white"
                    boxSize={5}
                    transition="all 0.2s"
                  />
                </Box>
              }
              role="group"
              onClick={() => handleMenuOpen(0, row.actualId)}
              px={1}
              m={0}
              height="auto"
              minHeight="20px"
              color={selectedMeltedRowId === row.actualId ? "white" : "black"}
              cursor="pointer"
              width="100%"
              textAlign="left"
              display="flex"
              justifyContent="space-between"
              bg={selectedMeltedRowId === row.actualId ? "purple.700" : "inherit"}
              wordBreak="break-word"
              whiteSpace="normal"
              _hover={{ bg: !treePannelLoader ? "purple.700" : "inherit", color: !treePannelLoader ? "white" : "black" }}
              isDisabled={treePannelLoader}
            >
              <Text
                as="span" 
                fontSize={isChatOpen ? "xs" : "sm"} 
                m={0} 
                ml={1} 
                p={0}
                textAlign="left"
                display="block"
                width="100%"
              >
                {row.metricName}{" "}
                {actualsIdNameMap && actualsIdNameMap[row.actualId].type === "metric" && (
                  <Tooltip
                      label={showFilterForMetric(row.actualId, node.current_node?.kg_observation!)}
                      placement="top"          
                  >
                      <Box
                          display="inline-flex"
                          position="relative"
                          zIndex={10}         
                          ml="2px"
                          pointerEvents="auto" 
                      >
                          <IconButton
                              aria-label="Filters"
                              icon={<FiFilter size="10px" />}
                              size="xs"
                              variant="ghost"
                              minW="16px"
                              h="16px"
                              p={0}
                          />
                      </Box>
                  </Tooltip>
                )}
              </Text>
            </MenuButton>
          </Menu>
        ) : (
          <Text
            as="span" 
            fontSize={isChatOpen ? "xs" : "sm"} 
            m={0} 
            ml={1} 
            p={0}
            textAlign="left"
            display="block"
            width="100%"
          >
            {row.metricName}{" "}
            {actualsIdNameMap && actualsIdNameMap[row.actualId].type === "metric" && (
              <Tooltip
                  label={showFilterForMetric(row.actualId, node.current_node?.kg_observation!)}
                  placement="top"          
              >
                  <Box
                      display="inline-flex"
                      position="relative"
                      zIndex={10}         
                      ml="2px"
                      pointerEvents="auto" 
                  >
                      <IconButton
                          aria-label="Filters"
                          icon={<FiFilter size="10px" />}
                          size="xs"
                          variant="ghost"
                          minW="16px"
                          h="16px"
                          p={0}
                      />
                  </Box>
              </Tooltip>
            )}
          </Text>
        )}
      </Td>
    );
    
    // Actual Value cell
    cells.push(
      <Td
        key="actual"
        color="inherit"
        textAlign="right"
        p={0}
        pr={2}
        m={0}
        width="140px"
        minWidth="120px"
        maxWidth="180px"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        position="static"
        left="auto"
        zIndex="auto"
        cursor="auto"
        bg="inherit"
      >
        {formatWithProto(row.actualsValue[0], formatProto, { 
          isMaterial: isMaterialColumn(row.actualId, node.current_node) 
        })}
      </Td>
    );

    // Material cell - only show if any metric has materials AND expanded
    if (hasMaterials && expandedActual) {
      const actualData = actualsMap[row.actualId];
      const materialValue = actualData?.material_value ? actualData.material_value[0] : null;
      // Get the material format directly from the parent metric's material definition
      const parentMetric = node.current_node?.kg_observation?.metrics.find(m => m.header?.id === row.actualId);
      const materialFormatProto = parentMetric?.material?.format;
      cells.push(
        <Td
          key="material"
          color="inherit"
          textAlign="right"
          p={0}
          pr={2}
          m={0}
          width="130px"
          minWidth="110px"
          maxWidth="150px"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          position="static"
          left="auto"
          zIndex="auto"
          cursor="auto"
          bg="inherit"
        >
          {materialValue !== null ? `${formatWithProto(materialValue, materialFormatProto, { isMaterial: true })} (${materialName})` : "-"}
        </Td>
      );
    }

    // Analysis cells - show if any metric has analysis data
    if (hasAnalysis) {
      // Collect all analysis types across all rows
      const analysisTypes = new Set<string>();
      meltedRows.forEach(r => {
        if (r.analysisValue) {
          Object.keys(r.analysisValue).forEach(type => {
            analysisTypes.add(type);
          });
        }
      });

      // Add cells for analysis values
      const sortedAnalysisTypes = sortAnalysisTypes(analysisTypes);
      sortedAnalysisTypes.forEach(analysisType => {
        const value = row.analysisValue?.[analysisType]?.[0];
        cells.push(
          <Td
            key={`analysis-${analysisType}`}
            color={value < 0 ? "red.500" : value >= 0 ? "green.500" : "inherit"}
            textAlign="right"
            p={0}
            pr={2}
            m={0}
            width="120px"
            minWidth="100px"
            maxWidth="140px"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            position="static"
            left="auto"
            zIndex="auto"
            cursor="auto"
            bg="inherit"
          >
            {formatWithProto(
              value,
              getMetricFormat(row.actualId, node.current_node),
              {
                isCompositionPercentage: analysisType === "COMPOSITION_PERCENTAGE",
                isRank: analysisType === "RANK",
                isAnalysisColumnType: true
              }
            )}
          </Td>
        );
      });
    }
    
    // Add cells for all benchmarks
    Array.from(benchmarkIds).forEach(benchmarkId => {
      // Benchmark Value cell
      cells.push(
        <Td
          key={`benchmark-${benchmarkId}`}
          color="inherit"
          textAlign="right"
          p={0}
          pr={2}
          m={0}
          width="130px"
          minWidth="110px"
          maxWidth="150px"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          position="static"
          left="auto"
          zIndex="auto"
          cursor="auto"
          bg="inherit"
        >
          {row.benchmarks[benchmarkId]
            ? formatWithProto(row.benchmarks[benchmarkId].benchmark_value[0], formatProto)
            : "-"}
        </Td>
      );

      // If expanded, add analysis value cells for this benchmark
      if (expandedBenchmarks[benchmarkId] && row.benchmarks[benchmarkId]?.analysis_value) {
        const analysisTypes = new Set<string>();
        Object.keys(row.benchmarks[benchmarkId].analysis_value).forEach(type => {
          analysisTypes.add(type);
        });

        // Add cells for analysis values
        const sortedAnalysisTypes = sortAnalysisTypes(analysisTypes);
        sortedAnalysisTypes.forEach(analysisType => {
          const value = row.benchmarks[benchmarkId].analysis_value[analysisType]?.[0];
          cells.push(
            <Td
              key={`${benchmarkId}-${analysisType}`}
              color={value < 0 ? "red.500" : value >= 0 ? "green.500" : "inherit"}
              textAlign="right"
              p={0}
              pr={2}
              m={0}
              width="120px"
              minWidth="100px"
              maxWidth="140px"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              position="static"
              left="auto"
              zIndex="auto"
              cursor="auto"
              bg="inherit"
            >
              {formatWithProto(
                value,
                getMetricFormat(row.actualId, node.current_node),
                {
                  isVariancePercentage: analysisType === "VARIANCE_PERCENTAGE",
                  isAccomplishPercentage: analysisType === "ACCOMPLISH_PERCENTAGE",
                  isCompositionPercentage: analysisType === "COMPOSITION_PERCENTAGE",
                  isRank: analysisType === "RANK",
                  isPercentageMetricVariance: (analysisType === "VARIANCE" && !!node.current_node.kg_observation?.metrics.find(m => m.header?.id === row.actualId)?.format?.percentage),
                  isAnalysisColumnType: true
                }
              )}
            </Td>
          );
        });
      }
    });
    
    return cells;
  };

  return (
    <TableContainer
      p={0}
      key={node?.current_node?.id}
      flex="1"
      display="flex"
      flexDirection="column"
      maxHeight={isOverlay ? "calc(70vh - 100px)" : "300px"}
      maxWidth="100%"
      overflowY="auto"
      overflowX="auto"
      position="relative"
      sx={{
        scrollbarWidth: "thin",
        scrollbarColor: "#CBD5E0 transparent",
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#CBD5E0",
          borderRadius: "8px",
        },
        "&:hover": {
          cursor: "default"
        },
        // Fix for sticky headers
        "& thead": {
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "white"
        },
        // Smooth horizontal scrolling
        "& table": {
          minWidth: "max-content"
        }
      }}
      m={1}
    >
      <Table 
        size={isChatOpen ? "sm" : "md"} 
        variant="simple" 
        w="100%" 
        align="center"
        sx={{
          tableLayout: "auto",
          minWidth: "max-content",
          borderCollapse: "separate",
          borderSpacing: 0
        }}
      >
            <Thead>
              <Tr>
                {renderHeaderColumns()}
              </Tr>
            </Thead>
            <Tbody>
              {sortedMeltedRows.map((row) => (
                <Tr 
                  key={row.actualId}
                  bg="bg.surface"
                  _hover={{ bg: "gray.100" }}
                >
                  {renderRowCells(row)}
                </Tr>
              ))}
            </Tbody>
          </Table>
    </TableContainer>
  );
};

export default MeltedDataTable;